import { useEffect, useRef, useState } from "react";

import isEqual from "lodash/isEqual";

import { translate, getI18nParams } from "app/filters/i18nFilter";
import { getFallbackChips } from "app/pages/activation/components/SetupModal/CompetitorsStep/constants";

import { getCompetitorsMap, getDomainsList } from "../../components/SetupModal/CompetitorsStep/helpers";
import { competitorsStepTexts } from "../../components/SetupModal/CompetitorsStep/texts";
import type { CompetitorsMap, CompetitorSchema, PromptSchema } from "../../components/SetupModal/CompetitorsStep/types";
import { PromptSource } from "../../components/SetupModal/CompetitorsStep/types";
import { useAISuggestionQuery } from "../../queries/setup/useAISuggestionQuery";
import { useIconsQuery } from "../../queries/useIconsQuery";
import { activationTracking } from "../../services/tracking";
import { useToasts } from "../helpers/useToasts";

export const useAISuggestions = (
    selectedPrimaryEntityName: string | undefined,
    country: number,
    promptObj: PromptSchema,
    selectedCompetitors: CompetitorSchema[],
    resetPrompt: () => void,
    savedThreadId: string,
    setSavedThreadId: (id: string) => void,
) => {
    const track = activationTracking.setup;
    const i18nParams = getI18nParams();

    const prevRequestedImageData = useRef([]);
    const prevCompetitorsList = useRef([]);

    const savedDomainAndCountry = useRef({ country: undefined, selectedPrimaryEntityName: "" });

    const [suggestionsAI, setSuggestionsAI] = useState<CompetitorsMap>(new Map());
    const [lastProcessedPrompt, setLastProcessedPrompt] = useState<string>("");

    const { showSuccessToast } = useToasts();

    useEffect(() => {
        if (
            country !== savedDomainAndCountry.current?.country ||
            selectedPrimaryEntityName !== savedDomainAndCountry.current.selectedPrimaryEntityName
        ) {
            setSavedThreadId("");

            savedDomainAndCountry.current = {
                selectedPrimaryEntityName,
                country,
            };
        }
    }, [country, selectedPrimaryEntityName, savedDomainAndCountry]);

    const {
        refetch: reFetchCompetitors,
        data,
        isLoading,
        isFetching,
    } = useAISuggestionQuery(selectedPrimaryEntityName, country, promptObj?.prompt, savedThreadId, i18nParams?.locale);

    const isSuggestionLoading = isLoading || isFetching;

    const suggestedCompetitorsList = getDomainsList(data, selectedCompetitors);
    const selectedCompetitorsDomains = selectedCompetitors.map((competitor) => competitor.name);

    const {
        data: selectedCompetitorsIcons,
        isLoading: isIconsLoading,
        isFetching: isIconsFetching,
    } = useIconsQuery(selectedCompetitorsDomains, isSuggestionLoading);

    const isCompetitorsIconsLoading = isIconsLoading || isIconsFetching;

    useEffect(() => {
        if (!!country && !!selectedPrimaryEntityName && !savedThreadId) {
            reFetchCompetitors();
        }
    }, [savedThreadId]);

    useEffect(() => {
        const refetchOnChangePrompt = async () => {
            if (!promptObj || !savedThreadId) {
                return;
            }

            if (promptObj.source === PromptSource.Input) {
                track.trackAIRefineMessageManuallyType(promptObj.prompt);
            }

            if (promptObj.source === PromptSource.Chip) {
                track.trackAIRefineMessageSuggestionClick(promptObj.prompt);
            }

            setLastProcessedPrompt(promptObj.prompt);

            await reFetchCompetitors();

            if (promptObj.source === "input") {
                resetPrompt();
            }
        };

        void refetchOnChangePrompt();
    }, [promptObj]);

    useEffect(() => {
        if (isSuggestionLoading || isCompetitorsIconsLoading || !data) {
            return;
        }

        const previousSelectedCompetitors = [];

        setSavedThreadId(data.threadId);

        const newSuggestedCompetitors = data.competitors.map((competitor) => ({
            ...competitor,
            image: competitor.favicon,
            isAiGenerated: data.isAiGenerated,
            name: competitor.domain,
        }));

        if (selectedCompetitorsIcons && selectedCompetitors.length) {
            selectedCompetitors.forEach((competitor) => {
                if (competitor.name !== selectedPrimaryEntityName) {
                    previousSelectedCompetitors.push({
                        ...competitor,
                        image: competitor.image || selectedCompetitorsIcons[competitor.name],
                    });

                    return;
                }
            });
        }

        setSuggestionsAI(getCompetitorsMap([...previousSelectedCompetitors, ...newSuggestedCompetitors]));

        prevRequestedImageData.current = suggestedCompetitorsList.newSuggestion;

        const isEqualSuggestions = isEqual(prevCompetitorsList.current, suggestedCompetitorsList.newSuggestion);

        if (prevCompetitorsList.current.length && isEqualSuggestions) {
            showSuccessToast(translate(competitorsStepTexts.aiSameResponseToastText));

            return;
        }

        prevCompetitorsList.current = suggestedCompetitorsList.newSuggestion;

        const promptToShow = promptObj?.prompt || lastProcessedPrompt;

        if (promptToShow && !isEqualSuggestions) {
            showSuccessToast(
                `${translate(competitorsStepTexts.aiSucessToastText, { num: data.competitors.length })} ${promptToShow}`,
            );
        }
    }, [isSuggestionLoading, isCompetitorsIconsLoading, lastProcessedPrompt]);

    return {
        suggestionsAI,
        isLoading: isSuggestionLoading || isCompetitorsIconsLoading,
        suggestedPrompts: data?.suggestedPrompts ?? getFallbackChips(),
    };
};
